import { c as _c } from "react/compiler-runtime";
import { Button, Divider, Stack, Tooltip, Typography } from '@mui/material';
import type { Condition } from '@repo/api-gw-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { canonicalOperator, relationOf } from '@/components/conditions/OperatorSelect';
import { createEmptyCondition } from '@/components/conditions/conditionFactory';
import type { DateFilterConfig } from '@/components/dateTimeFilter/dateFilterTypes';
import { DateTimeFilterControls } from '@/components/dateTimeFilter/dateTimeFilterControls';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { asPickedCondition, asStoredCondition, pickedConditionValues, type FilterProperty } from '@/types/advanceFilter';
import { buildFilterPropertyOptions } from './filterPropertyMenuOptions';

// Every part of the strip is pinned to a band this tall — the height of a single tag row — so wrapped tag
// rows extend downward and the first row keeps its position instead of drifting up as the strip grows.
export const FILTER_ROW_HEIGHT = '32px';

/** Lets the tag's hover rule see that the pointer is on the clear icon rather than on the tag body. */
const CLEAR_HOVER_CLASS = 'filter-tag-clear';

/**
 * Writes the strip's root condition. `replace` asks a URL-backed owner to overwrite the current history entry
 * instead of pushing one: a panel writes every edit as it is made, and one open-and-pick session should still be a
 * single step for the back button.
 */
export type SetRootCondition = (condition: Condition, options?: {
  replace?: boolean;
}) => void;

/**
 * How a tag looks once it is the one being acted on — its panel open, or its clear icon under the pointer. The extra
 * border pixel comes out of the padding, so the tag keeps its width and never nudges its neighbours.
 */
const ACTIVE_TAG_STYLES = {
  paddingLeft: '11px',
  paddingRight: '3px',
  borderWidth: '2px',
  borderColor: 'var(--mui-palette-text-primary)',
  backgroundColor: 'transparent'
};

// A chip stands for a property, not for a condition: a property that declares
// companions owns one condition per companion and shows them under a single
// tag (data classes and the entity types inside them).
const ownedNames = (property: FilterProperty) => [property.name, ...(property.condition?.companion?.properties ?? [])];

// Compares conditions by what they filter on rather than by identity, so a
// selection made in a different order than the default still counts as the
// default and leaves Reset with nothing to undo.
const conditionKey = (condition: Condition) => JSON.stringify({
  property: condition.property,
  operator: condition.operator,
  value: [...(condition.value ?? [])].sort(),
  conditions: condition.conditions
});
const withValues = (conditions: Condition[]) => conditions.filter(c => c.value?.length);
const sameConditions = (a: Condition[], b: Condition[]) => {
  if (a.length !== b.length) {
    return false;
  }
  const keysA = a.map(conditionKey).sort();
  const keysB = b.map(conditionKey).sort();
  return keysA.every((key, index) => key === keysB[index]);
};
export const FilterStrip = ({
  rootCondition,
  setRootCondition,
  supportedFilterProperties,
  defaultConditions,
  groupProperties = false,
  isPageDataLoaded = true,
  dateFilter
}: {
  rootCondition: Condition;
  setRootCondition: SetRootCondition;
  supportedFilterProperties: FilterProperty[];
  /**
   * The property filters the page opens with. Reset restores these rather than
   * leaving the surface unfiltered, and it hides itself while they are exactly
   * what is applied. Omitted means the page has no default and Reset clears.
   */
  defaultConditions?: Condition[];
  /**
   * Sorts the add-filter menu into a fly-out per property category. Worth it only
   * where the list is long enough to be worth narrowing first — a short one reads
   * faster flat, which is why it is off unless a surface asks.
   */
  groupProperties?: boolean;
  /**
   * Holds back the checks that hide an empty property from the add-filter menu,
   * so they never compete with the page's own request. Until this turns true no
   * property is checked, and an unchecked property stays visible — pass it once
   * that request has resolved.
   */
  isPageDataLoaded?: boolean;
  /**
   * Seats a date filter at the head of the strip, ahead of the property filters
   * it is divided from. Its conditions ride in `rootCondition` with all the
   * others; naming its properties here is what keeps them out of the chips, so
   * the date section stays the only thing that edits them directly. Reset still
   * covers them — it restores the whole strip, both halves.
   */
  dateFilter?: DateFilterConfig;
}) => {
  // The chip whose panel is open. It may have nothing in the query — a property just added, or a selection just
  // emptied — so it carries what the panel last emitted (an operator picked before any value lives only here) and
  // where it sits, to hold that place while it is empty. `hasPushed` is the session's history state: the first
  // write pushes an entry and the rest replace it, so Back undoes the whole session.
  const [openChip, setOpenChip] = useState<{
    key: string;
    index: number;
    hasPushed: boolean;
    emptyConditions?: Condition[];
  }>();
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isPointerOverAdd, setIsPointerOverAdd] = useState(false);
  const [emptyPropertyNames, setEmptyPropertyNames] = useState<Set<string>>(new Set());

  // Memoised for its own sake: the `?? []` mints a fresh array whenever the root
  // condition carries none, which every dependent memo below would see as a change.
  const committedConditions = useMemo(() => rootCondition.conditions ?? [], [rootCondition.conditions]);

  // The date section owns the conditions on the properties it offers: they sit in
  // the same root condition, but the chips read the property filters alone, so
  // neither half edits the other's conditions. One list drives both what the
  // section renders and what it owns.
  const dateOwnedNames = useMemo(() => new Set(dateFilter?.properties.map(property => property.value)), [dateFilter?.properties]);
  const {
    dateConditions,
    propertyConditions
  } = useMemo(() => {
    const isDateOwned = (condition: Condition) => !!condition.property && dateOwnedNames.has(condition.property);
    return {
      dateConditions: committedConditions.filter(isDateOwned),
      propertyConditions: committedConditions.filter(condition => !isDateOwned(condition))
    };
  }, [committedConditions, dateOwnedNames]);
  const {
    defaultDateConditions,
    defaultPropertyConditions
  } = useMemo(() => {
    const isDateOwned = (condition: Condition) => dateOwnedNames.has(condition.property ?? '');
    return {
      defaultDateConditions: (defaultConditions ?? []).filter(isDateOwned),
      defaultPropertyConditions: (defaultConditions ?? []).filter(condition => !isDateOwned(condition))
    };
  }, [defaultConditions, dateOwnedNames]);

  // The date section is at its default when both halves of its state are: the
  // conditions, and the preset key it stores separately. The key can move on its
  // own — picking "between" before entering any date sets it and leaves no
  // condition behind — so reading the conditions alone would call a filter the
  // user has visibly changed unchanged.
  const isDefaultApplied = sameConditions(propertyConditions, defaultPropertyConditions) && sameConditions(dateConditions, defaultDateConditions) && !dateFilter?.relativeKey;
  const isCommitted = useCallback((property: FilterProperty) => committedConditions.some(c => ownedNames(property).includes(c.property ?? '')), [committedConditions]);

  // A panel closes itself as a click lands elsewhere, and that click may already have opened another chip's panel —
  // clearing unconditionally would shut the new one before it renders.
  const closeIfOpen = (key: string) => setOpenChip(current => current?.key === key ? undefined : current);
  const onConditionsChange = (ownedPropertyNames: string[], next: Condition[], options?: {
    replace?: boolean;
  }) => {
    const applied = withValues(next);
    const conditions: Condition[] = [];
    let replaced = false;

    // Rebuilt in place rather than filtered-then-appended, so editing a chip
    // leaves the strip's order alone instead of sending it to the end.
    for (const condition of committedConditions) {
      if (!ownedPropertyNames.includes(condition.property ?? '')) {
        conditions.push(condition);
        continue;
      }
      if (!replaced) {
        conditions.push(...applied);
        replaced = true;
      }
    }
    setRootCondition({
      ...rootCondition,
      conditions: replaced ? conditions : [...conditions, ...applied]
    }, options);
  };

  // Every edit in an open panel is written to the query as it is made. Only value-bearing conditions go there,
  // so a value-less edit stays on the open chip for its panel to show, and an edit that leaves the query as it is
  // does not count as the session's history push.
  const onChipEdit = (key: string, ownedPropertyNames: string[], next: Condition[]) => {
    const applied = withValues(next);
    setOpenChip(current => current?.key === key ? {
      ...current,
      emptyConditions: applied.length ? undefined : next
    } : current);
    const committedOwned = committedConditions.filter(c => ownedPropertyNames.includes(c.property ?? ''));
    if (sameConditions(applied, committedOwned)) {
      return;
    }
    onConditionsChange(ownedPropertyNames, next, {
      replace: openChip?.key === key && openChip.hasPushed
    });
    setOpenChip(current => current?.key === key ? {
      ...current,
      hasPushed: true
    } : current);
  };
  const availableFilterProperties = useMemo(() => supportedFilterProperties.filter(property => !property.condition?.hideChip && !isCommitted(property) && property.name !== openChip?.key && !emptyPropertyNames.has(property.name)), [supportedFilterProperties, isCommitted, openChip?.key, emptyPropertyNames]);
  const ownerOf = useCallback((condition: Condition) => supportedFilterProperties.find(property => ownedNames(property).includes(condition.property ?? '')), [supportedFilterProperties]);

  // A condition can reach the strip spelled the way it was stored — from a link,
  // a saved view — and the strip is what turns it into an API filter, so an
  // outdated spelling has to be corrected in the query itself. Relabelling it for
  // the picker would leave the chip reading as applied over an unfiltered list.
  useEffect(() => {
    const canonical = committedConditions.map(condition => {
      const property = ownerOf(condition);
      const operator = property ? canonicalOperator(condition.operator, relationOf(property)) : condition.operator;
      return operator === condition.operator ? condition : {
        ...condition,
        operator
      };
    });
    if (canonical.some((condition, i) => condition !== committedConditions[i])) {
      setRootCondition({
        ...rootCondition,
        conditions: canonical
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rootCondition is rewritten here; depending on it would re-run this on its own write.
  }, [committedConditions, ownerOf]);
  const onPropertyEmptyChange = (name: string, isEmpty: boolean) => setEmptyPropertyNames(current => {
    const alreadyMarked = current.has(name);
    if (isEmpty === alreadyMarked) {
      return current;
    }
    const next = new Set(current);
    if (isEmpty) {
      next.add(name);
    } else {
      next.delete(name);
    }
    return next;
  });
  const chips = useMemo(() => {
    const built: {
      key: string;
      property?: FilterProperty;
      ownedPropertyNames: string[];
      conditions: Condition[];
      pending: boolean;
    }[] = [];
    for (const condition of propertyConditions) {
      const property = ownerOf(condition);
      if (property?.condition?.hideChip) {
        continue;
      }
      const key = property?.name ?? condition.property ?? '';
      const chip = built.find(x => x.key === key);
      if (chip) {
        chip.conditions.push(condition);
        continue;
      }
      built.push({
        key,
        property,
        // A condition no supported property claims still gets a tag of its own, so
        // a filter arriving from a link is visible and can be cleared.
        ownedPropertyNames: property ? ownedNames(property) : [key],
        conditions: [condition],
        pending: false
      });
    }

    // The open chip shows (at count 0) even while nothing of it is in the query: a just-picked property appears at
    // once with its panel open, so adding a filter is a single step, and a chip whose last value was unticked holds
    // the place it had until its panel closes. Either way the chip goes once the panel closes or is cleared.
    const openProperty = openChip && !built.some(chip => chip.key === openChip.key) ? supportedFilterProperties.find(property => property.name === openChip.key) : undefined;
    if (openChip && openProperty) {
      built.splice(Math.min(openChip.index, built.length), 0, {
        key: openProperty.name,
        property: openProperty,
        ownedPropertyNames: ownedNames(openProperty),
        conditions: openChip.emptyConditions ?? [createEmptyCondition(openProperty)],
        pending: true
      });
    }
    return built;
  }, [propertyConditions, ownerOf, openChip, supportedFilterProperties]);
  return <Stack data-testid='filter-strip' direction='row' divider={<Divider orientation='vertical' flexItem variant='middle' sx={{
    marginY: '8px'
  }} />} sx={{
    alignItems: 'flex-start',
    gap: '24px'
  }}>
      {dateFilter && <Stack direction='row' data-testid='filter-strip-date-filter' sx={{
      flexShrink: 0,
      alignItems: 'center',
      minHeight: FILTER_ROW_HEIGHT
    }}>
          <DateTimeFilterControls {...dateFilter} conditions={dateConditions} onConditionsChange={next => setRootCondition({
        ...rootCondition,
        conditions: [...next, ...propertyConditions]
      })} />
        </Stack>}
      {/* One flex item, so the divider the container draws lands between the date section and all of the property filtering rather than inside it. */}
      <Stack direction='row' sx={{
      alignItems: 'flex-start',
      gap: '12px',
      minWidth: 0
    }}>
        {isPageDataLoaded && supportedFilterProperties.filter(property => property.condition?.useOptions).map(property => <PropertyAvailabilityProbe key={property.name} property={property} onEmptyChange={onPropertyEmptyChange} />)}
        {/* The label is its own flex item so it keeps its place on a single line; everything else lives in the wrapping row. */}
        <Stack sx={{
        flexShrink: 0,
        justifyContent: 'center',
        minHeight: FILTER_ROW_HEIGHT
      }}>
          <Typography>Filter by</Typography>
        </Stack>
        <Stack direction='row' sx={{
        alignItems: 'center',
        alignContent: 'flex-start',
        gap: '12px',
        flexWrap: 'wrap',
        // minWidth 0 lets this row shrink below its content width, which is what makes the tags wrap
        // once it runs out of space.
        minWidth: 0
      }}>
          {chips.map(({
          key,
          property,
          ownedPropertyNames,
          conditions,
          pending
        }, i) => <FilterTag key={key} testId={key} conditions={conditions} property={property} isOpen={openChip?.key === key} onOpen={() => setOpenChip({
          key,
          index: i,
          hasPushed: false
        })} onEdit={next => onChipEdit(key, ownedPropertyNames, next)} onClose={() => closeIfOpen(key)} onClear={() => {
          closeIfOpen(key);
          if (pending) {
            return;
          }
          setRootCondition({
            ...rootCondition,
            conditions: committedConditions.filter(c => !ownedPropertyNames.includes(c.property ?? ''))
          });
        }} />)}
          <Stack className='relative' data-testid='filter-add'>
            {/* Held open by pointer movement rather than by the hover MUI tracks itself: dismissing a panel drops the
                pending tag and slides this button along the re-flowing strip, which reads as an enter under a pointer
                that never moved. Movement is what separates the two. Movement while a panel is open counts as leaving
                — crossing the button on the way to dismiss that panel is not a hover of it, and the re-flow that
                follows owes no matching leave to undo one. */}
            {availableFilterProperties.length > 0 && <Tooltip open={isPointerOverAdd && !isAddMenuOpen && !openChip} title='Add filter'>
                <span onMouseMove={() => setIsPointerOverAdd(!isAddMenuOpen && !openChip)} onMouseLeave={() => setIsPointerOverAdd(false)} onFocus={() => setIsPointerOverAdd(true)} onBlur={() => setIsPointerOverAdd(false)}>
                  <OptionMenu popperClassName='mt-[-32px]!' placement='bottom-start' iconClassName='text-(--mui-palette-text-primary)' icon='material-symbols-add-rounded' disableTransition
              // The strip sits at the top of a scroll container, so on a
              // page whose content is short — a table with no rows — the
              // property list is taller than the container and would be
              // cropped by it.
              disablePortal={false} onToggle={setIsAddMenuOpen} onOptionSelected={option => {
                // A pick inside a fly-out bubbles through the category rows
                // that contain it; those rows open a sub-menu rather than
                // select a property.
                if (typeof option === 'object' && option.subMenu) {
                  return;
                }
                const displayName = typeof option === 'string' ? option : option.id;
                const picked = supportedFilterProperties.find(x => x.displayName === displayName);
                if (picked) {
                  setOpenChip({
                    key: picked.name,
                    index: chips.length,
                    hasPushed: false
                  });
                }
              }} options={buildFilterPropertyOptions(availableFilterProperties, supportedFilterProperties, groupProperties)} />
                </span>
              </Tooltip>}
          </Stack>
          {/* Kept as one flex item inside the wrapping row, so the divider never strands on a line without Reset. */}
          {!isDefaultApplied && <Stack direction='row' sx={{
          alignItems: 'center',
          gap: '24px',
          flexShrink: 0,
          minHeight: FILTER_ROW_HEIGHT,
          marginLeft: '12px'
        }}>
              <Divider orientation='vertical' flexItem variant='middle' sx={{
            marginY: '8px'
          }} />
              <Button variant='text' data-testid='filter-strip-reset' onClick={() => {
            setOpenChip(undefined);
            setRootCondition({
              ...rootCondition,
              conditions: [...defaultDateConditions, ...defaultPropertyConditions]
            });
            // The preset key lives outside the conditions, so restoring
            // those alone would leave the section showing a span its
            // conditions no longer describe.
            dateFilter?.onRelativeKeyChange(undefined);
          }}>
                Reset
              </Button>
            </Stack>}
        </Stack>
      </Stack>
    </Stack>;
};

/**
 * Reads a property's option source to find out whether it has anything to offer,
 * and renders nothing. It is mounted only once the page's own data has loaded,
 * so it never competes with that request; until then the property is simply not
 * reported on, which leaves it visible.
 */
const PropertyAvailabilityProbe = ({
  property,
  onEmptyChange
}: {
  property: FilterProperty;
  onEmptyChange: (name: string, isEmpty: boolean) => void;
}) => {
  const {
    options,
    isLoading
  } = property.condition!.useOptions!({
    condition: createEmptyCondition(property),
    onChange: () => undefined,
    property
  });
  const isEmpty = options.length === 0;
  useEffect(() => {
    if (!isLoading) {
      onEmptyChange(property.name, isEmpty);
    }
  }, [isLoading, isEmpty, property.name, onEmptyChange]);
  return null;
};
const FilterTag = ({
  onClear,
  testId,
  conditions,
  property,
  onEdit,
  onClose,
  isOpen,
  onOpen
}: {
  onClear: () => void;
  /** Every edit made in the panel, as it is made; the panel stays open. */
  onEdit: (conditions: Condition[]) => void;
  onClose: () => void;
  testId: string;
  conditions: Condition[];
  property: FilterProperty | undefined;
  isOpen: boolean;
  onOpen: () => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // The panel is not modal and its click-away deliberately skips this chip, so the chip is what dismisses it.
  const togglePanel = () => isOpen ? onClose() : onOpen();

  // The condition's own label leads, for a property whose filter needs naming more precisely than its column
  // header does. A condition no supported property claims still gets a chip, and it may carry no name at all —
  // better a bare count than the string "undefined".
  const entityName = property?.condition?.displayName || property?.displayName || conditions[0]?.property || '';
  // A chip owning companions counts across all of them — three entity types read as three.
  const selectedValueCount = conditions.reduce((total, condition) => total + pickedConditionValues(condition, property).length, 0);
  return <>
      <Stack ref={setAnchorEl} data-testid={`filter-tag-${testId}`} direction='row' onClick={togglePanel} className='inline-flex rounded-[var(--mui-shape-customBorderRadius-sm)] relative cursor-pointer' divider={<Divider orientation='vertical' sx={{
      height: '12px',
      alignSelf: 'center',
      borderColor: 'var(--mui-palette-divider)'
    }} />} sx={{
      alignItems: 'center',
      gap: '12px',
      height: FILTER_ROW_HEIGHT,
      paddingLeft: '12px',
      paddingRight: '4px',
      border: '1px solid var(--mui-palette-text-primary)',
      // Hovering a closed tag trades its outline for a fill; the border stays transparent rather than being dropped so the tag holds its size.
      ...(isOpen ? ACTIVE_TAG_STYLES : {
        '&:hover': {
          backgroundColor: 'var(--mui-palette-primary-hover)',
          borderColor: 'var(--mui-palette-primary-hover)'
        },
        // A pointer on the clear icon means "drop this filter", not "open it", so the tag takes the same
        // look as an open one and leaves the hover to the icon's own highlight.
        [`&:hover:has(.${CLEAR_HOVER_CLASS}:hover)`]: ACTIVE_TAG_STYLES
      })
    }}>
        <Typography component='span' variant='subtitle2' className='text-ellipsis overflow-hidden whitespace-nowrap	max-w-[200px]'>
          {`${entityName} (${selectedValueCount})`}
        </Typography>
        <Tooltip title='Clear'>
          <Stack data-testid={`filter-tag-${testId}-clear`} className={`${CLEAR_HOVER_CLASS} cursor-pointer`} onClick={event => {
          event.stopPropagation();
          onClear();
        }} sx={{
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
          '&:hover': {
            backgroundColor: 'var(--mui-palette-primary-hover)'
          }
        }}>
            <i className='material-symbols-close-small-rounded text-xl' />
          </Stack>
        </Tooltip>
      </Stack>
      {isOpen && anchorEl && property?.condition && <RenderFilterComponent conditions={conditions} anchorEl={anchorEl} onClose={onClose} onChange={onEdit} property={property} />}
    </>;
};
const RenderFilterComponent = t0 => {
  const $ = _c(15);
  const {
    conditions,
    property,
    anchorEl,
    onChange,
    onClose
  } = t0;
  let t1;
  if ($[0] !== anchorEl || $[1] !== onClose) {
    t1 = {
      anchorEl,
      onClose
    };
    $[0] = anchorEl;
    $[1] = onClose;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const panelHost = t1;
  const companion = property.condition?.companion;
  if (companion) {
    let t2;
    if ($[3] !== companion.Component || $[4] !== conditions || $[5] !== onChange || $[6] !== panelHost || $[7] !== property) {
      t2 = <companion.Component conditions={conditions} onChange={onChange} property={property} panelHost={panelHost} />;
      $[3] = companion.Component;
      $[4] = conditions;
      $[5] = onChange;
      $[6] = panelHost;
      $[7] = property;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  const FilterComponent = property.condition?.Component;
  let t2;
  if ($[9] !== FilterComponent || $[10] !== conditions || $[11] !== onChange || $[12] !== panelHost || $[13] !== property) {
    const condition = conditions.find(c => c.property === property.name) ?? createEmptyCondition(property);
    t2 = FilterComponent && <FilterComponent condition={asPickedCondition(condition, property)} onChange={next => onChange([asStoredCondition(next, property), ...conditions.filter(c_0 => c_0.property !== property.name)])} property={property} panelHost={panelHost} optionScope="discovered" />;
    $[9] = FilterComponent;
    $[10] = conditions;
    $[11] = onChange;
    $[12] = panelHost;
    $[13] = property;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
};